import Tooltip from '@material-ui/core/Tooltip';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import ModalContainer from '@components/modals/ModalContainer';

import ModalHeader from '@features/assets/components/CommonComponents/ModalHeader';
import SingleListItem from '@features/assets/components/CommonComponents/SingleListItem';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import {
	downloadArtworkImages,
	downloadSketchImages,
	getArtworkResponse,
	getSketchResponse
} from '@services/assetsService';

import { downloadBlobFIle, getShortName } from '@utils/Util';

import { DOC_TYPE } from '@/features/assets/constants';
import CommonImage from '@/components/CommonImage';

const FileViewModal = ({ viewType, selectedItemId, onClose }) => {
	const [selectedFileInfo, setSelectedFileInfo] = useState(null);
	const [selectedImage, setSelectedImage] = useState([]);
	const [allFilesAndImages, setAllFilesAndImages] = useState([]);
	const [allImages, setAllImages] = useState([]);
	const [allFiles, setAllFiles] = useState([]);
	const dispatch = useDispatch();

	useEffect(() => {
		onFetchData();
	}, []);

	const onFetchData = async () => {
		dispatch(openLoader());
		let data;
		if (viewType === 'SKETCH') {
			data = await getSketchResponse(selectedItemId);
		} else {
			data = await getArtworkResponse(selectedItemId);
		}
		setSelectedFileInfo(data);
		setAllFilesAndImages(data.documentResponseList);
		dispatch(closeLoader());
	};

	useEffect(() => {
		if (allFilesAndImages?.length > 0) {
			const images = allFilesAndImages.filter((doc) => doc.docType.startsWith('ASSETS_IMAGE'));
			const files = allFilesAndImages.filter((doc) => doc.docType === 'ASSETS_FILE');
			const orderedImages = [
				...images.filter((doc) => doc.docType === DOC_TYPE.ASSETS_IMAGE_S1),
				...images.filter((doc) => doc.docType === DOC_TYPE.ASSETS_IMAGE_S2),
				...images.filter((doc) => doc.docType === DOC_TYPE.ASSETS_IMAGE_S3),
				...images.filter((doc) => doc.docType === DOC_TYPE.ASSETS_IMAGE_S4)
			];
			setAllImages(orderedImages);
			setAllFiles([...files]);
		}
	}, [allFilesAndImages]);

	const onDownloadImages = async () => {
		dispatch(openLoader());
		try {
			let data;
			if (viewType === 'SKETCH') {
				data = await downloadSketchImages(selectedItemId);
			} else {
				data = await downloadArtworkImages(selectedItemId);
			}

			downloadBlobFIle(data);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const onGetCurrentImage = (id) => selectedImage?.find((image) => image?.id === id)?.imageIndex || 0;

	return (
		<ModalContainer className="category-pom-modal" onCloseModal={() => {}}>
			<div className="common-popup category-pom-modal">
				<ModalHeader
					referenceNumber={selectedFileInfo?.referenceNumber}
					marketName={selectedFileInfo?.marketResponse?.name}
					categoryName={selectedFileInfo?.categoryResponse?.name}
					mood={selectedFileInfo?.moodTagResponse?.text}
					onClose={onClose}
				/>
				<div className="common-popup-body product-slider-body shadow-1dp">
					<div className="product-slider-section assets-slide-section scroll-y-label">
						<div className="product-information d-flex justify-content-between align-items-start gap-4">
							<div className="product-list d-flex">
								<div className="slider-nav d-flex flex-column justify-content-start">
									<div className="items">
										{allImages?.map((item, idx) => (
											<div
												className={`cursor-pointer nav-single d-flex justify-content-center align-items-center ${
													onGetCurrentImage(selectedFileInfo?.id) === idx
														? 'active'
														: ''
												}`}
												key={item?.id}
												onClick={() => {
													setSelectedImage([
														{ id: selectedFileInfo?.id, imageIndex: idx }
													]);
												}}
											>
												<CommonImage
													src={item?.docUrl}
													width="60px"
													alt="product"
													isStatic={false}
												/>
											</div>
										))}
									</div>
								</div>
								<div className="pom-product-slider">
									<div className="product-single text-center">
										<CommonImage
											src={allImages[onGetCurrentImage(selectedFileInfo?.id)]?.docUrl}
											alt="product"
										/>
										<div
											className="download-option cursor-pointer"
											onClick={onDownloadImages}
										>
											<CommonImage
												src={`/icons/download-gray-dark-02.svg`}
												alt
												width="24px"
												height="24px"
											/>
										</div>
									</div>
								</div>
							</div>
							<div className="slider-info">
								<div className="product-description">
									<ul>
										{selectedFileInfo?.referenceNumber && (
											<SingleListItem
												title={`${
													viewType === 'SKETCH' ? 'Sketch No.' : 'Artwork No.'
												}`}
												value={selectedFileInfo?.referenceNumber}
											/>
										)}
										{selectedFileInfo?.marketResponse?.name && (
											<SingleListItem
												title="Market"
												value={selectedFileInfo?.marketResponse?.name}
											/>
										)}

										{selectedFileInfo?.categoryResponse?.name && (
											<SingleListItem
												title="Category"
												value={selectedFileInfo?.categoryResponse?.name}
											/>
										)}
										{selectedFileInfo?.moodTagResponse?.text && (
											<SingleListItem
												title="Mood"
												value={selectedFileInfo?.moodTagResponse?.text}
											/>
										)}
										{selectedFileInfo?.colorResponseList?.length > 0 && (
											<li className="regular-12 d-flex justify-content-start">
												<span className="subtitle gray_dark_05">Color</span>
												<span className="clone">:</span>
												<div className="sub-info gray_dark_01 weight-500">
													<ul>
														{selectedFileInfo?.colorResponseList?.map(
															(item, idx) => (
																<li
																	className="d-flex align-items-center align-self-center gap-2 pb-1"
																	key={`color_response_${idx}`}
																>
																	<span
																		className="color-icon"
																		style={{
																			background: item.hexCode
																		}}
																	/>
																	<span className="color-code">
																		{item.code}
																	</span>
																</li>
															)
														)}
													</ul>
												</div>
											</li>
										)}

										{selectedFileInfo?.artworkPatternTagResponse?.text && (
											<SingleListItem
												title="Artwork Pattern"
												value={selectedFileInfo?.artworkPatternTagResponse?.text}
											/>
										)}

										{selectedFileInfo?.fittingTypeTagResponse?.text && (
											<SingleListItem
												title="Fitting type"
												value={selectedFileInfo?.fittingTypeTagResponse?.text}
											/>
										)}
										{selectedFileInfo?.riseTagResponse?.text && (
											<SingleListItem
												title="Rise"
												value={selectedFileInfo?.riseTagResponse?.text}
											/>
										)}
										{selectedFileInfo?.lengthTagResponse?.text && (
											<SingleListItem
												title="Length"
												value={selectedFileInfo?.lengthTagResponse?.text}
											/>
										)}
										{selectedFileInfo?.inspiredFrom && (
											<SingleListItem
												title="Inspired from"
												value={selectedFileInfo?.inspiredFrom}
											/>
										)}

										{selectedFileInfo?.tagResponseList?.length > 0 && (
											<SingleListItem
												title="Tag"
												isTagList
												tagList={selectedFileInfo?.tagResponseList || []}
											/>
										)}

										{selectedFileInfo?.remarks && (
											<SingleListItem
												title="Remarks"
												value={selectedFileInfo?.remarks}
											/>
										)}
										{allFiles?.length > 0 && (
											<li className="regular-12 d-flex justify-content-start">
												<span className="subtitle gray_dark_05">Files</span>
												<span className="clone">:</span>
												<div className="sub-info gray_dark_01 weight-500">
													<ul className="psd-file-list">
														{allFiles.map((item, idx) => (
															<Tooltip
																title={item?.name}
																placement="top"
																arrow
																key={`tool_tip_${idx}`}
															>
																<li
																	className="mb-1 cursor-pointer"
																	onClick={() =>
																		window.open(item?.docUrl, '_blank')
																	}
																>
																	{getShortName(item?.name, 20)}
																</li>
															</Tooltip>
														))}
													</ul>
												</div>
											</li>
										)}
									</ul>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</ModalContainer>
	);
};

export default FileViewModal;
